import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import {
  getCSSColorText,
  getCSSSectionHeader,
  getCSSSizeLgText,
  getCSSSizeMdText,
  getCSSSizeSmText,
} from '~/utilities/getCSS';
import { AppDataContext } from '~/contexts/AppDataContext';
import { getFontSizeObject } from '~/utilities/getFontSizeObject';
import { getSectionHeaderObject } from '~/utilities/getSectionHeaderObject';
import { ArticleContext } from '~/contexts/ArticleContext';
import JsonLd from '~/components/Shared/Utility/JsonLd/JsonLd';
import CardImage from '~/components/Layout/Body/Page/CardImage';
import CardText from '~/components/Layout/Body/Page/CardText';
import { getArticleAuthors } from '~/utilities/getArticleAuthors';
import { useIsSIWG } from '~/utilities/useIsSI';

const sectionCSS = cva('py-2.5 px-2.5 max-w-[1080px] mx-auto', {
  variants: {
    ...getCSSSectionHeader(),
  },
});

const headingCSS = cva('mb-4', {
  variants: {
    color: getCSSColorText(),
    sizeSm: getCSSSizeSmText(),
    sizeMd: getCSSSizeMdText(),
    sizeLg: getCSSSizeLgText(),
  },
});

export default component$((): JSX.Element => {
  const {
    themeComponents: { relatedPosts, sectionHeader },
    payloadToSSR: { pageContent },
  } = useContext(AppDataContext);

  const isSIWG = useIsSIWG();
  const article = useContext(ArticleContext);
  const related = pageContent.additionalArticles?.feed.value.articles;

  if (
    isSIWG ||
    !related ||
    !related.length ||
    article?.finiteScrollNumber !== 0
  )
    return <></>;

  const featuredArticle = related[0];
  const {
    title: featTitle,
    articleUrl: featArticleUrl,
    cover: featCover,
  } = featuredArticle;
  const featAuthors = featuredArticle.authors.additional[0]
    ? [featuredArticle.authors.additional[0]]
    : getArticleAuthors(featuredArticle.authors);

  return (
    <aside
      class={sectionCSS({
        ...getSectionHeaderObject(sectionHeader),
      })}
    >
      <h2
        class={headingCSS({
          color: relatedPosts.titleColor.value,
          ...getFontSizeObject(relatedPosts, 'titleFontSize'),
        })}
      >
        {pageContent.additionalArticles?.title.value || 'Featured Stories'}
      </h2>

      <div class='grid gap-5'>
        <article
          key={featArticleUrl}
          class='card relative box-border group/card'
        >
          <JsonLd type='articleEach' data={featuredArticle} />
          <CardImage
            cover={featCover}
            coverType='image'
            imageWidths={{
              small: 400,
              medium: 800,
              large: 1000,
            }}
            isSingle={false}
            imageAspectRatio='16/9'
            wrapperClass='relative min-w-[100px]'
          />
          <CardText
            title={featTitle}
            showDescription={false}
            showAuthor={true}
            authors={featAuthors}
            showDate={false}
            showImage={true}
            showMainCategory={false}
            isSingle={true}
            titleSize='normal'
            wrapperClass='px-5 pb-4 pt-10 absolute left-0 right-0 bottom-0 overflow-hidden text-white [&_.card-footer]:!text-lightGrey bg-gradient-to-b from-[rgba(0,0,0,0)] to-black'
          />
          <a
            href={featArticleUrl}
            class='absolute inset-0 w-full h-full'
            title={featTitle}
          />
        </article>
        <div class='grid grid-cols-1 md:grid-cols-3 gap-5'>
          {related.map((item, index) => {
            if (index === 0) return null; // Skip the first item as it's already displayed

            const { title, articleUrl, cover, seoDescription } = item;
            const authors = item.authors.additional[0]
              ? [item.authors.additional[0]]
              : getArticleAuthors(item.authors);

            return (
              <article
                key={item.articleUrl}
                class='card relative box-border group/card flex gap-2.5 flex-col border border-solid border-lightGrey'
              >
                <JsonLd type='articleEach' data={item} />
                <CardImage
                  cover={cover}
                  coverType='image'
                  imageWidths={{
                    small: 400,
                    medium: 400,
                    large: 500,
                  }}
                  isSingle={false}
                  imageAspectRatio='16/9'
                  wrapperClass='relative min-w-[100px]'
                />
                <CardText
                  title={title}
                  description={seoDescription}
                  showDescription={true}
                  showAuthor={true}
                  authors={authors}
                  showDate={false}
                  showImage={true}
                  showMainCategory={false}
                  isSingle={true}
                  titleSize='normal'
                  wrapperClass='flex flex-col flex-1 mx-4 my-5'
                />
                <a
                  href={articleUrl}
                  class='absolute inset-0 w-full h-full'
                  title={title}
                />
              </article>
            );
          })}
        </div>
      </div>
    </aside>
  );
});
